
import Vue from 'vue'
import api from 'Api'
import jwtDecode from 'jwt-decode'
import router from '@/router'
import isBase64 from 'is-base64'

const state = {
  user: JSON.parse(localStorage.getItem('reveles-user')) || {},
  showStatusList: false,
  statusList: [],
  signinLoading: false,
  userLoading: false,
  signinError: false
}

const getters = {
  user: state => {
    return state.user
  },
  status: state => {
    return state.showStatusList
  },
  statusList: state => {
    return state.statusList
  },
  signinLoading: state => {
    return state.signinLoading
  },
  userLoading: state => {
    return state.userLoading
  },
  signinError: state => {
    return state.signinError
  }
}

function getAvatarUrl (event, name, bool) {
  function hashCode (str) { // java String#hashCode
    var hash = 0
    if (str?.length) {
      for (var i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
      }
    }
    return hash
  }

  const src = 'https://avatar.oxro.io/avatar?name=' + name?.split(' ').join('+') + '&background=3ACA56' + '&color=212338' + '&bold=true'

  if (bool) {
    return src
  } else {
    event.target.src = src
  }
}

function clearLocalStorageForUser (state) {
  state.user = null
  localStorage.removeItem('reveles-user')
  localStorage.removeItem('reveles-expires-at')
  localStorage.removeItem('reveles-currentOrganization')
}

function setUserLocalStorage (state, userData) {
  const user = JSON.parse(localStorage.getItem('reveles-user'))
  user.email = userData.email
  user.picture = userData.picture
  user.name = userData.name
  user.affiliate_id = userData.affiliate_id
  user.nickname = userData.nickname
  user.entity_id = userData.entity_id
  user.user_id = userData.user_id
  user.email_verified = userData.email_verified
  user.eula_accepted = userData.eula_accepted
  user.email_notifications = userData.email_notifications
  user.tour_complete = userData.tour_complete
  user.hasBillingInfo = userData.hasBillingInfo
  user.role = userData.role || {}
  user.poc = userData.poc
  state.user = user
  localStorage.setItem('reveles-user', JSON.stringify(user))
}

const actions = {
  signUpUserApi (context, payload) {
    const signupData = { ...payload }
    signupData.email = payload.username
    return api(false).post('/signup', signupData)
  },
  signInUserApi (context, payload) {
    context.state.signinError = false
    const user = payload
    context.state.signinLoading = true
    api(false).post('/login', {
      username: user.username,
      password: user.password
    }).then(async function (response) {
      const userData = response.data
      if (userData.error) {
        context.commit('setStatusOfList', false)
        state.loginDisabled = false
        context.state.signinError = true
        context.commit('apiError', userData.error)
      } else {
        userData.redirect = user.redirect
        userData.poc = user.poc
        await Promise.all([context.dispatch('getUserLogin', userData), context.dispatch('loadOrganizations')])
      }
      context.state.signinLoading = false
    }).catch(error => {
      context.state.signinError = true
    }).finally(() => {
      if (user.redirect) router.replace(user.redirect)
    })
  },
  signOutUserFromApi (context) {
    context.commit('logoutUser')
  },
  deleteAccount (context) {
    return api(false).delete('/user/delete').then((response) => {
      const userData = response.data
      if (userData.error) {
        context.commit('apiError', userData.error)
      } else {
        context.commit('deleteAccount')
      }
    })
  },
  resetPassword (context, email) {
    return api(false).post('/password-reset', {
      email: email
    }).then(function (response) {
      const userData = response.data
      if (userData.error) {
        context.commit('apiError', userData.error)
      } else {
        context.commit('resetPasswordSuccess', userData)
      }
    })
  },
  sendPOCPasswordLink (context, email) {
    return api(false).post('/password-reset', { email: email }).then((response) => {
      const data = response.data
      if (data.error) {
        context.commit('apiError', data.error)
      } else {
        context.commit('apiSuccess', 'An email has been sent to you, for creating password.')
      }
    })
  },
  updateProfile (context, profile) {
    const { data, shouldReload } = profile
    let gravatar
    if (data.picture && data.picture.includes('s.gravatar.com')) {
      gravatar = data.picture
    }
    if (data.picture !== undefined && !isBase64(data.picture, { allowMime: true })) {
      delete data.picture
    }
    api(false).put('/user', data).then(function (response) {
      const userData = response.data
      if (userData.error) {
        context.commit('apiError', userData.error)
      } else {
        // Need to add get params t=Date.now() for override browser cache of images
        userData.picture = gravatar || process.env.VUE_APP_STATIC + '/profile_pictures/' + userData.user_id + '/profile_pic.png?t=' + Date.now()
        context.commit('profileSuccess', { data: userData, shouldReload })
      }
    }).catch(error => {
      context.state.signinError = true
    })
  },
  async getUserLogin (context, user) {
    const mixpanel = Vue.prototype.$mixpanel

    delete user.redirect
    delete user.password
    if (Vue.prototype.$useAuth0Login && !Vue.prototype.$auth.isAuthenticated) {
      Vue.prototype.$auth.loginWithTokens(user)
    }
    localStorage.setItem('reveles-user', JSON.stringify(user))
    setTimeout(function () {
      context.commit('setStatusList', { id: 1, text: 'Setting up your' })
    }, 1000)
    const response = await api(false).get('/user')
    const userData = response.data
    if (!Object.prototype.hasOwnProperty.call(userData, 'picture') && Object.prototype.hasOwnProperty.call(userData, 'avatar_url')) {
      userData.picture = userData.avatar_url
    } else {
      userData.picture = process.env.VUE_APP_STATIC + '/profile_pictures/' + userData.user_id + '/profile_pic.png'
    }
    mixpanel.identify(userData.entity_id)
    mixpanel.people.set({
      $email: userData.email,
      name: userData.name,
      USER_ID: userData.entity_id
    })
    mixpanel.track('Login')
    setUserLocalStorage(state, userData)
    const token = user.id_token
    const decoded = jwtDecode(token)
    const expiresAt = decoded.exp * 1000
    localStorage.setItem('reveles-expires-at', expiresAt)

    if (!context.state.user.eula_accepted) {
      context.commit('setStatusList', { id: 2, text: 'Logging you in' })
    }
    return JSON.parse(localStorage.getItem('reveles-user'))
  },
  setUser (context, userData) {
    setUserLocalStorage(state, userData)
  },
  setEula ({ commit }) {
    commit('SET_EULA')
  },
  checkPassword (context, password) {
    return api(false).post('/password-breached', { password })
  },

  async validateUserEmail (context, payload) {
    const response = await api(false).post('/validate', {
      email: payload.email
    })
    const responseData = response.data
    if (responseData.error) {
      context.commit('apiError', responseData.error)
    } else {
      return responseData.success
    }
  }
}

const mutations = {
  loginUserSuccess (state, user) {
  },
  apiError (state, error) {
    Vue.notify({
      group: 'messages',
      type: 'error',
      duration: 5000,
      text: error
    })
  },
  apiSuccess (state, message) {
    Vue.notify({
      group: 'messages',
      type: 'success',
      duration: 5000,
      text: message
    })
  },
  logoutUser (state, reload) {
    clearLocalStorageForUser(state)
  },
  deleteAccount (state) {
    clearLocalStorageForUser(state)
    window.location = '/session/login'
  },
  logoutPoc (state) {
    clearLocalStorageForUser(state)
  },
  resetPasswordSuccess (state) {
    if (!state.user) {
      router.push('/session/login')
    }
    clearLocalStorageForUser(state)
    setTimeout(function () {
      router.push('/session/login')
      Vue.notify({
        group: 'messages',
        type: 'success',
        text: 'You should receive an email with password reset instructions.'
      })
    }, 1500)
  },
  profileSuccess (state, user) {
    const { data, shouldReload } = user
    const mixpanel = Vue.prototype.$mixpanel
    mixpanel.track('Profile Updated')
    setUserLocalStorage(state, data)
    Vue.notify({
      group: 'messages',
      type: 'success',
      text: 'Profile Updated'
    })
    if (!shouldReload) return
    location.reload()
  },
  SET_EULA (state) {
    const mixpanel = Vue.prototype.$mixpanel
    const user = JSON.parse(localStorage.getItem('reveles-user'))
    state.user = { ...state.user, eula_accepted: true }
    localStorage.setItem('reveles-user', JSON.stringify({ ...user, eula_accepted: true }))
    mixpanel.track('Eula Accepted')
  },
  setStatusList (state, data) {
    state.statusList.push(data)
  },
  setStatusOfList (state, data) {
    state.showStatusList = data
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
